import React, { FC, ReactNode } from 'react';
import styles from '../journey-explorer.module.scss';
import { displayValue } from '../journey-explorer-utils';

export const Dot: FC<{ color: string; size?: number }> = ({ color, size = 8 }) => (
	<span className={styles.dot} style={{ width: size, height: size, background: color }} />
);

export const Highlight: FC<{ text: string; term?: string }> = ({ text, term }) => {
	const needle = term?.trim().toLowerCase();
	if (!needle) {
		return <>{text}</>;
	}
	const parts: ReactNode[] = [];
	const haystack = text.toLowerCase();
	let start = 0;
	let index = haystack.indexOf(needle);
	while (index !== -1) {
		parts.push(text.slice(start, index));
		parts.push(
			<mark key={index} className={styles.search_match}>
				{text.slice(index, index + needle.length)}
			</mark>
		);
		start = index + needle.length;
		index = haystack.indexOf(needle, start);
	}
	parts.push(text.slice(start));
	return <>{parts}</>;
};

interface FilterGroupProps {
	label: string;
	color: string;
	count: number;
	open: boolean;
	onToggle: () => void;
	search?: string;
	children: ReactNode;
}

export const FilterGroup: FC<FilterGroupProps> = ({ label, color, count, open, onToggle, search, children }) => (
	<div>
		<button
			type="button"
			aria-expanded={open}
			className={`${styles.group_header} ${open ? styles.group_header_open : ''}`}
			style={{ borderLeftColor: count ? color : 'transparent' }}
			onClick={onToggle}
		>
			<span className={styles.group_arrow}>
				<span className={`${styles.chevron} ${open ? styles.chevron_down : ''}`} />
			</span>
			<Dot color={color} />
			<span className={`${styles.group_label} ${open || count ? styles.group_label_active : ''}`}>
				<Highlight text={label} term={search} />
			</span>
			{count > 0 && (
				<span className={styles.badge} style={{ background: `${color}20`, color }}>
					{count}
				</span>
			)}
		</button>
		{open && <div className={styles.group_body}>{children}</div>}
	</div>
);

interface DimensionOptionsProps {
	label: string;
	values: (string | null)[];
	selected: string[];
	color: string;
	search?: string;
	onToggle: (value: string) => void;
}

export const DimensionOptions: FC<DimensionOptionsProps> = ({ label, values, selected, color, search, onToggle }) => (
	<div className={styles.dimension}>
		<div className={styles.dimension_label}>
			<Highlight text={label} term={search} />
		</div>
		{values.map((raw) => {
			const value = displayValue(raw);
			const checked = selected.includes(value);
			return (
				<button
					key={value}
					type="button"
					role="checkbox"
					aria-checked={checked}
					className={`${styles.option} ${checked ? styles.option_selected : ''}`}
					style={checked ? { color } : undefined}
					onClick={() => onToggle(value)}
				>
					<span
						className={styles.checkbox}
						style={checked ? { borderColor: color, background: `${color}25`, color } : undefined}
					>
						{checked ? '✓' : ''}
					</span>
					<Highlight text={value} term={search} />
				</button>
			);
		})}
	</div>
);
